import React, { useContext } from 'react';
import * as yup from 'yup';
import PropTypes from 'prop-types';
import trans from 'translate';
import { connect } from 'react-redux';
import config from '../../../../../../../../../config/config.json';
import {
  CUSTOM_TOEKN_ADDRESS_FOR_CLOSE_ORDER_KEY,
  manualOrdersMapperSchema,
  manualOrdersTypes,
  typeMapperKeys,
  vpoolTypesMapper,
} from '../../../../../../../../../constants/vpool';
import { usePromise } from '../../../../../../../../../hooks/usePromise/usePromise';
import { assetAmountInWei } from '../../../../../../../../../services/assetsService';
import { getBaseToken } from '../../../../../../../../../services/createHPoolService';
import { ManualOrderContext } from '../../../../../../../../Modals/ManualOrderModals/ManualOrderModals';
import { getSignatureConfig, updateTradeOrder } from '../../../../../../../../../services/hpoolService';
import { approvedAmountOnOrder, sendManualSwaps } from '../../../../../../../../../services/vpoolService';
import { signTypedDataV4 } from '../../../../../../../../../services/web3Service';
import { combineLoadingMsgs, getTokenSymbol } from '../../../../../../../../../utils/format';
import {
  getValueByMultiProps,
  multiConver,
  objectMapper,
  removeObjKeys,
} from '../../../../../../../../../utils/mapper';
import { createValidation } from '../../../../../../../../../utils/validation';
import { Button, Fields } from '../../../../../../../../Common';
import WalletValidator from '../../../../../../../../hoc/WalletValidator/WalletValidator';
import { OrdersContext } from '../../../../../../../../Modals/EditTokensModal/EditOrderDetails';
import SelectField from '../../../../../../../../Common/Forms/Fields/SelectField';
import { closeOrderFields } from './fields';
import { getTokenDecimals } from '../../../../../../../../../services/utilsService';

const closeOrderValidationSchema = createValidation({
  totalTokensToBeReceived: yup.string().required(' '),
  finalTokenPrice: yup.string().required(' '),
  public_price: yup.string().required(' '),
  end_of_cliff: yup.mixed().test('len', 'Invalid timestamp', (val) => `${val}`?.length === 10 || !+val || val === null), // allow zero value since it's the default from BE side
  end_of_vesting: yup
    .mixed()
    .test('len', 'Invalid timestamp', (val) => `${val}`?.length === 10 || !+val || val === null),
  amountOfTokensToReceiveNow: yup.string().required(' '),

  lp_address: yup
    .string()
    .required(' ')
    .min(42, 'Invalid Token address')
    .max(42, 'Invalid Token address')
    .required(' '),

  tokenAddress: yup
    .string()
    .required(' ')
    .min(42, 'Invalid Token address')
    .max(42, 'Invalid Token address')
    .required(' '),

  master_address: yup
    .string()
    .required(' ')
    .min(42, 'Invalid Token address')
    .max(42, 'Invalid Token address')
    .required(' '),
});

function CloseOrder({ poolAddress, account }) {
  const { network: chainId, stableCoins } = config;
  const { selectedToken, hpool } = useContext(OrdersContext);

  const { orderInfo, passedValues, onSubmit = () => {} } = useContext(ManualOrderContext);

  const { token_id: passedTokenId, orderId: passedOrderId } = passedValues ?? {};

  const [sign, , signing] = usePromise(signTypedDataV4, { showError: true });
  const [sendOrder, , sendingOrder] = usePromise(sendManualSwaps, { showError: true });
  const [updateOrder, , updatingOrder] = usePromise(updateTradeOrder, { showError: true });
  const [signAmount, , signAmountLoading] = usePromise(approvedAmountOnOrder, {
    showSuccess: trans('vpool.signature_completed'),
  });

  const { id: poolId, assets = [] } = hpool;
  const tokenAddress = getValueByMultiProps(selectedToken, ['address', 'token_address']); // TODO integrate this when u have real values
  const tokenToReceiveSymbol = 'lst' || getTokenSymbol(selectedToken);
  const stableCoin = getBaseToken(assets, stableCoins);
  const stableCoinSymbol = getTokenSymbol(stableCoin);

  const currentLoading = combineLoadingMsgs([
    { isLoading: signing, loadingText: 'Signing' },
    { isLoading: sendingOrder, loadingText: 'Creating Order' },
    { isLoading: updatingOrder, loadingText: 'Updating Order' },
    { isLoading: signAmountLoading, loadingText: 'Approving' },
  ]);

  const createOrder = (values) => {
    const orderType = manualOrdersTypes.MANUAL_CLOSE_ORDER.toLowerCase();
    const body = {
      ...values,
      type: orderType,
      vpoolId: poolId,
      chainId,
    };
    const mappedForRequest = objectMapper(body, manualOrdersMapperSchema);
    return sendOrder(mappedForRequest);
  };

  const updateStoredCustomToken = (tokenAddress) => {
    localStorage.setItem(CUSTOM_TOEKN_ADDRESS_FOR_CLOSE_ORDER_KEY, tokenAddress);
  };

  const handleSubmit = async (values) => {
    const { type, primaryType } = getSignatureConfig('CloseOrder', vpoolTypesMapper);
    const tokenDecimals = await getTokenDecimals(values.tokenAddress);
    const busdDecimals = await getTokenDecimals(config.busd);

    const approvedAmount = assetAmountInWei(values.amountOfTokensToReceiveNow, tokenDecimals);

    values = multiConver(
      [
        {
          keys: ['unLockingTime'],
          convert: (value) => +value,
        },
      ],
      values,
    );

    // Approve the spending of selected tokenAddress
    await signAmount(account, approvedAmount, values.tokenAddress, poolAddress);

    const { trade_order: tradeOrder = orderInfo } = orderInfo ? {} : (await createOrder(values)) || {};
    const { id: tradeOrderId = passedOrderId, token_id: tokenId = passedTokenId } = tradeOrder;
    let msg = { tokenId, ...values };

    // Prepare the msg to be valid for EIP217
    msg = removeObjKeys(msg, typeMapperKeys('CloseOrder'));

    msg = multiConver(
      [
        {
          keys: ['tokenId'], // conver tokenId to number before signing
          convert: (value) => +value,
        },
        {
          keys: ['totalTokensToBeReceived', 'amountOfTokensToReceiveNow'], // conver these Object keys values to wei before signing
          convert: (value) => assetAmountInWei(+value, tokenDecimals),
        },
        {
          keys: ['finalTokenPrice'], // conver these Object keys values to wei before signing
          convert: (value) => assetAmountInWei(+value, busdDecimals), // BUSD decimals
        },
      ],
      removeObjKeys(msg, typeMapperKeys('CloseOrder')), // => remove unncessary obj key
    );

    const signature = await sign(msg, type, primaryType, true);
    updateOrder(tradeOrderId, { signature, delete: false }).then(onSubmit);
  };

  const { onPrev } = useContext(OrdersContext);

  const handleClick = () => {
    onPrev();
    onSubmit();
  };

  return (
    <Fields
      fields={closeOrderFields}
      validationSchema={closeOrderValidationSchema}
      onSubmit={handleSubmit}
      initialValues={passedValues}
    >
      {({ disabled, errors, defaultWrapperClass, values }) => (
        <div className={defaultWrapperClass}>
          <Button className="button back" onClick={handleClick} text="common.cancel" />
          <WalletValidator disabled>
            <Button
              type="submit"
              className="button green"
              text="common.create"
              loadingText={currentLoading.loadingText}
              loading={currentLoading.isLoading}
              disabled={disabled || currentLoading.isLoading}
              requireWallet
            />
          </WalletValidator>
        </div>
      )}
    </Fields>
  );
}
CloseOrder.defaultProps = {
  // orderInfo: null,
  // passedValues: null,
};

CloseOrder.propTypes = {
  poolAddress: PropTypes.string.isRequired,
  account: PropTypes.string.isRequired,
  // orderInfo: PropTypes.object,
  // passedValues: PropTypes.object,
};

const mapStateToProps = ({ createHPool, wallet }) => ({
  poolAddress: createHPool.hpool.contract_address,
  account: wallet.account,
});

export default connect(mapStateToProps)(CloseOrder);
